import moment from "moment";

/**
 * Mock data generator for the reporting page
 * Simulates delivered sales order data with revenues and quantities
 */

// Mock customers
export const mockCustomers = [
  { id: 1, name: "Acme Corporation" },
  { id: 2, name: "Global Industries Ltd" },
  { id: 3, name: "Tech Solutions Inc" },
  { id: 4, name: "Manufacturing Co" },
  { id: 5, name: "Retail Partners Group" },
  { id: 6, name: "Distribution Network" },
  { id: 7, name: "Enterprise Systems" },
  { id: 8, name: "Supply Chain Masters" },
];

// Mock product categories
export const mockCategories = [
  { id: 1, name: "Foam Products" },
  { id: 2, name: "Milling Services" },
  { id: 3, name: "Custom Seats" },
  { id: 4, name: "Raw Materials" },
];

/**
 * Generate mock weekly data for a given year
 */
export function generateWeeklyData(year = moment().year()) {
  const data = [];
  const startOfYear = moment()
    .year(year)
    .startOf("year");
  const weeks = 52;

  for (let week = 0; week < weeks; week++) {
    const weekStart = startOfYear.clone().add(week, "weeks");

    mockCustomers.forEach((customer) => {
      // Random revenue between 5000 and 50000
      const revenue = Math.random() * 45000 + 5000;
      // Random quantity between 100 and 1000
      const quantity = Math.floor(Math.random() * 900 + 100);

      data.push({
        period: weekStart.format("YYYY-[W]WW"),
        week: week + 1,
        startDate: weekStart.format("YYYY-MM-DD"),
        endDate: weekStart
          .clone()
          .endOf("week")
          .format("YYYY-MM-DD"),
        customerId: customer.id,
        customerName: customer.name,
        revenue: Math.round(revenue * 100) / 100,
        quantity: quantity,
        categoryId:
          mockCategories[Math.floor(Math.random() * mockCategories.length)].id,
      });
    });
  }

  return data;
}

/**
 * Generate mock monthly data for a given year
 */
export function generateMonthlyData(year = moment().year()) {
  const data = [];
  const months = 12;

  for (let month = 0; month < months; month++) {
    const monthStart = moment()
      .year(year)
      .month(month)
      .startOf("month");

    mockCustomers.forEach((customer) => {
      // Higher revenue for monthly (aggregate of ~4 weeks)
      const revenue = Math.random() * 180000 + 20000;
      const quantity = Math.floor(Math.random() * 3600 + 400);

      data.push({
        period: monthStart.format("YYYY-MM"),
        month: month + 1,
        monthName: monthStart.format("MMMM"),
        startDate: monthStart.format("YYYY-MM-DD"),
        endDate: monthStart
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD"),
        customerId: customer.id,
        customerName: customer.name,
        revenue: Math.round(revenue * 100) / 100,
        quantity: quantity,
        categoryId:
          mockCategories[Math.floor(Math.random() * mockCategories.length)].id,
      });
    });
  }

  return data;
}

/**
 * Generate mock yearly data
 */
export function generateYearlyData(startYear = moment().year() - 5, years = 6) {
  const data = [];

  for (let i = 0; i < years; i++) {
    const year = startYear + i;

    mockCustomers.forEach((customer) => {
      // Higher revenue for yearly (aggregate of 12 months)
      const revenue = Math.random() * 2000000 + 200000;
      const quantity = Math.floor(Math.random() * 40000 + 5000);

      data.push({
        period: year.toString(),
        year: year,
        startDate: moment()
          .year(year)
          .startOf("year")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .year(year)
          .endOf("year")
          .format("YYYY-MM-DD"),
        customerId: customer.id,
        customerName: customer.name,
        revenue: Math.round(revenue * 100) / 100,
        quantity: quantity,
        categoryId:
          mockCategories[Math.floor(Math.random() * mockCategories.length)].id,
      });
    });
  }

  return data;
}

/**
 * Generate time series data for charts (aggregated by period)
 */
export function generateTimeSeriesData(
  periodType = "month",
  year = moment().year()
) {
  let data = [];

  switch (periodType) {
    case "week":
      data = generateWeeklyData(year);
      break;
    case "month":
      data = generateMonthlyData(year);
      break;
    case "year":
      data = generateYearlyData(year - 5, 6);
      break;
  }

  // Aggregate by period
  const aggregated = {};
  data.forEach((item) => {
    if (!aggregated[item.period]) {
      aggregated[item.period] = {
        period: item.period,
        revenue: 0,
        quantity: 0,
        startDate: item.startDate,
      };
    }
    aggregated[item.period].revenue += item.revenue;
    aggregated[item.period].quantity += item.quantity;
  });

  return Object.values(aggregated).sort((a, b) =>
    moment(a.startDate).diff(moment(b.startDate))
  );
}

/**
 * Get customer breakdown data
 */
export function getCustomerBreakdown(
  periodType = "month",
  year = moment().year()
) {
  let data = [];

  switch (periodType) {
    case "week":
      data = generateWeeklyData(year);
      break;
    case "month":
      data = generateMonthlyData(year);
      break;
    case "year":
      data = generateYearlyData(year, 1);
      break;
  }

  // Aggregate by customer
  const aggregated = {};
  data.forEach((item) => {
    if (!aggregated[item.customerId]) {
      aggregated[item.customerId] = {
        customerId: item.customerId,
        customerName: item.customerName,
        revenue: 0,
        quantity: 0,
      };
    }
    aggregated[item.customerId].revenue += item.revenue;
    aggregated[item.customerId].quantity += item.quantity;
  });

  return Object.values(aggregated).sort((a, b) => b.revenue - a.revenue);
}

/**
 * Calculate period comparison metrics
 */
export function calculatePeriodMetrics(
  periodType = "month",
  year = moment().year()
) {
  const currentData = generateTimeSeriesData(periodType, year);
  const previousData = generateTimeSeriesData(periodType, year - 1);

  // Calculate totals
  const currentRevenue = currentData.reduce(
    (sum, item) => sum + item.revenue,
    0
  );
  const currentQuantity = currentData.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const previousRevenue = previousData.reduce(
    (sum, item) => sum + item.revenue,
    0
  );
  const previousQuantity = previousData.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  // Calculate deltas
  const revenueDelta = currentRevenue - previousRevenue;
  const revenuePercentChange =
    previousRevenue > 0 ? (currentRevenue / previousRevenue - 1) * 100 : 0;

  const quantityDelta = currentQuantity - previousQuantity;
  const quantityPercentChange =
    previousQuantity > 0 ? (currentQuantity / previousQuantity - 1) * 100 : 0;

  // YTD calculations (up to current month)
  const currentMonth = moment().month();
  const ytdCurrentData = currentData.slice(0, currentMonth + 1);
  const ytdPreviousData = previousData.slice(0, currentMonth + 1);

  const ytdCurrentRevenue = ytdCurrentData.reduce(
    (sum, item) => sum + item.revenue,
    0
  );
  const ytdPreviousRevenue = ytdPreviousData.reduce(
    (sum, item) => sum + item.revenue,
    0
  );
  const ytdRevenueChange =
    ytdPreviousRevenue > 0
      ? (ytdCurrentRevenue / ytdPreviousRevenue - 1) * 100
      : 0;

  const ytdCurrentQuantity = ytdCurrentData.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const ytdPreviousQuantity = ytdPreviousData.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const ytdQuantityChange =
    ytdPreviousQuantity > 0
      ? (ytdCurrentQuantity / ytdPreviousQuantity - 1) * 100
      : 0;

  return {
    current: {
      revenue: currentRevenue,
      quantity: currentQuantity,
    },
    previous: {
      revenue: previousRevenue,
      quantity: previousQuantity,
    },
    delta: {
      revenue: revenueDelta,
      revenuePercent: revenuePercentChange,
      quantity: quantityDelta,
      quantityPercent: quantityPercentChange,
    },
    ytd: {
      currentRevenue: ytdCurrentRevenue,
      previousRevenue: ytdPreviousRevenue,
      revenueChange: ytdRevenueChange,
      currentQuantity: ytdCurrentQuantity,
      previousQuantity: ytdPreviousQuantity,
      quantityChange: ytdQuantityChange,
    },
  };
}
